import { randomUUID } from "node:crypto";
import { promises as fs } from "node:fs";
import path from "node:path";

import type { Logger } from "pino";
import { z } from "zod";

import type { WorkspaceStack } from "../shared/messages.js";

/**
 * Tab stacks — named per-(userId, workspaceId) containers for sessions.
 *
 * Source of truth lives here, on the daemon, in a single JSON file at
 * `$APPOSTLE_HOME/workspace-stacks.json`. Stacks travel with the account
 * because the daemon is already shared across the user's devices; the
 * existing agent reconcile loop applies them to the tab bar.
 *
 * The agent itself carries `stackId: string | null` on its record so that
 * "which stack is each agent in" rides along on agent snapshots — no
 * separate sync needed for assignment. This service owns only the stack
 * metadata (name, order, active pointer).
 */

const PersistedStackSchema = z.object({
  id: z.string(),
  name: z.string(),
  order: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const PersistedScopeRecordSchema = z.object({
  userId: z.string(),
  workspaceId: z.string(),
  stacks: z.array(PersistedStackSchema),
  activeStackId: z.string().nullable(),
  seeded: z.boolean(),
});

type PersistedScopeRecord = z.infer<typeof PersistedScopeRecordSchema>;

const PersistedFileSchema = z.array(PersistedScopeRecordSchema);

/**
 * Starter stacks created on first access to a (user, workspace). The user
 * lands in Default (`activeStackId === null`). These names are seeded
 * once; deleting them all leaves the record empty without re-seeding.
 */
const STARTER_STACK_NAMES = ["Copywriting", "Refactoring", "Design", "Feature build"] as const;

export interface WorkspaceStacksSnapshot {
  stacks: WorkspaceStack[];
  activeStackId: string | null;
}

export class WorkspaceStacksService {
  private readonly filePath: string;
  private readonly logger: Logger;
  private loaded = false;
  /** Keyed by `${userId}:${workspaceId}`. */
  private readonly cache = new Map<string, PersistedScopeRecord>();
  private persistQueue: Promise<void> = Promise.resolve();

  constructor(options: { filePath: string; logger: Logger }) {
    this.filePath = options.filePath;
    this.logger = options.logger.child({ module: "workspace-stacks-service" });
  }

  async initialize(): Promise<void> {
    await this.load();
  }

  /**
   * Get the stacks + active stack pointer for a (user, workspace).
   * Seeds starter stacks on first access — subsequent calls with the
   * same key return the existing record, even if the user has deleted
   * all stacks (we don't re-seed empty records).
   */
  async getSnapshot(userId: string, workspaceId: string): Promise<WorkspaceStacksSnapshot> {
    await this.load();
    const record = await this.ensureSeeded(userId, workspaceId);
    return toSnapshot(record);
  }

  async createStack(
    userId: string,
    workspaceId: string,
    name: string,
  ): Promise<{ snapshot: WorkspaceStacksSnapshot; stack: WorkspaceStack }> {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new Error("Stack name is required");
    }
    await this.load();
    const record = await this.ensureSeeded(userId, workspaceId);
    const now = new Date().toISOString();
    const stack: WorkspaceStack = {
      id: `stk_${randomUUID().replace(/-/g, "").slice(0, 16)}`,
      name: trimmed,
      order: record.stacks.length,
      createdAt: now,
      updatedAt: now,
    };
    record.stacks = [...record.stacks, stack];
    await this.persist();
    return { snapshot: toSnapshot(record), stack };
  }

  async renameStack(
    userId: string,
    workspaceId: string,
    stackId: string,
    name: string,
  ): Promise<WorkspaceStacksSnapshot> {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new Error("Stack name is required");
    }
    await this.load();
    const record = await this.ensureSeeded(userId, workspaceId);
    const next = record.stacks.map((stack) =>
      stack.id === stackId
        ? { ...stack, name: trimmed, updatedAt: new Date().toISOString() }
        : stack,
    );
    if (next.every((stack, idx) => stack === record.stacks[idx])) {
      throw new Error(`Stack not found: ${stackId}`);
    }
    record.stacks = next;
    await this.persist();
    return toSnapshot(record);
  }

  /**
   * Remove a stack. Agents that were in it become unstacked — the caller
   * (Session) is responsible for invoking AgentManager.unsetStackForAgents
   * to flip their stackId to null and broadcast the resulting state.
   *
   * Also clears the active-stack pointer if it referenced the deleted
   * stack — the user falls back to Default rather than pointing at a
   * dangling id.
   */
  async deleteStack(
    userId: string,
    workspaceId: string,
    stackId: string,
  ): Promise<WorkspaceStacksSnapshot> {
    await this.load();
    const record = await this.ensureSeeded(userId, workspaceId);
    const next = record.stacks.filter((stack) => stack.id !== stackId);
    if (next.length === record.stacks.length) {
      throw new Error(`Stack not found: ${stackId}`);
    }
    // Re-pack order indices so the next create lands at the right end.
    record.stacks = next.map((stack, idx) => ({ ...stack, order: idx }));
    if (record.activeStackId === stackId) {
      record.activeStackId = null;
    }
    await this.persist();
    return toSnapshot(record);
  }

  async reorderStacks(
    userId: string,
    workspaceId: string,
    orderedStackIds: string[],
  ): Promise<WorkspaceStacksSnapshot> {
    await this.load();
    const record = await this.ensureSeeded(userId, workspaceId);
    const byId = new Map(record.stacks.map((stack) => [stack.id, stack]));
    if (orderedStackIds.length !== record.stacks.length) {
      throw new Error("Reorder requires the full set of stack ids");
    }
    const reordered: WorkspaceStack[] = [];
    const now = new Date().toISOString();
    for (let i = 0; i < orderedStackIds.length; i++) {
      const id = orderedStackIds[i];
      const existing = byId.get(id);
      if (!existing) {
        throw new Error(`Reorder references unknown stack: ${id}`);
      }
      reordered.push({ ...existing, order: i, updatedAt: now });
    }
    record.stacks = reordered;
    await this.persist();
    return toSnapshot(record);
  }

  async setActiveStack(
    userId: string,
    workspaceId: string,
    stackId: string | null,
  ): Promise<WorkspaceStacksSnapshot> {
    await this.load();
    const record = await this.ensureSeeded(userId, workspaceId);
    if (stackId !== null && !record.stacks.some((stack) => stack.id === stackId)) {
      throw new Error(`Stack not found: ${stackId}`);
    }
    const beforeMutation = record.activeStackId;
    record.activeStackId = stackId;
    const afterMutation = record.activeStackId;
    this.logger.info(
      {
        userId,
        workspaceId,
        stackId,
        beforeMutation,
        afterMutation,
        recordIdentity: (record as { __debugId?: number }).__debugId,
        cacheSize: this.cache.size,
        sameReference: this.cache.get(scopeKey(userId, workspaceId)) === record,
      },
      "setActiveStack:pre-persist",
    );
    await this.persist();
    // Re-read after persist to confirm the cache still holds the new value
    const postPersistRecord = this.cache.get(scopeKey(userId, workspaceId));
    this.logger.info(
      {
        userId,
        workspaceId,
        cacheActiveStackId: postPersistRecord?.activeStackId,
        sameRefAsBefore: postPersistRecord === record,
      },
      "setActiveStack:post-persist",
    );
    return toSnapshot(record);
  }

  /**
   * Validates that a stackId belongs to the (user, workspace) scope.
   * `null` is always valid (Default). Used by Session at agent-create
   * time so an agent can't be assigned to a stack from a different
   * user's namespace.
   */
  async validateStackBelongs(
    userId: string,
    workspaceId: string,
    stackId: string | null,
  ): Promise<boolean> {
    if (stackId === null) return true;
    await this.load();
    const record = this.cache.get(scopeKey(userId, workspaceId));
    if (!record) return false;
    return record.stacks.some((stack) => stack.id === stackId);
  }

  private async ensureSeeded(userId: string, workspaceId: string): Promise<PersistedScopeRecord> {
    const key = scopeKey(userId, workspaceId);
    const existing = this.cache.get(key);
    if (existing) return existing;
    const now = new Date().toISOString();
    const record: PersistedScopeRecord = {
      userId,
      workspaceId,
      stacks: STARTER_STACK_NAMES.map((name, idx) => ({
        id: `stk_${randomUUID().replace(/-/g, "").slice(0, 16)}`,
        name,
        order: idx,
        createdAt: now,
        updatedAt: now,
      })),
      activeStackId: null,
      seeded: true,
    };
    this.cache.set(key, record);
    await this.persist();
    return record;
  }

  private async load(): Promise<void> {
    if (this.loaded) return;
    try {
      const raw = await fs.readFile(this.filePath, "utf8");
      const parsed = PersistedFileSchema.parse(JSON.parse(raw));
      for (const record of parsed) {
        this.cache.set(scopeKey(record.userId, record.workspaceId), record);
      }
    } catch (error) {
      const code = (error as NodeJS.ErrnoException).code;
      if (code !== "ENOENT") {
        this.logger.error(
          { err: error, filePath: this.filePath },
          "Failed to load workspace-stacks file",
        );
      }
    }
    this.loaded = true;
  }

  private async persist(): Promise<void> {
    const next = this.persistQueue.then(() => this.writeSnapshotToDisk());
    this.persistQueue = next.catch(() => {});
    await next;
  }

  private async writeSnapshotToDisk(): Promise<void> {
    const records = Array.from(this.cache.values());
    const appostle = records.find(
      (r) => r.workspaceId === "/Users/nico/Dropbox/_Development/appostle",
    );
    this.logger.info(
      {
        recordCount: records.length,
        appostleActiveStackId: appostle?.activeStackId,
        appostleSeeded: appostle?.seeded,
      },
      "writeSnapshotToDisk",
    );
    await fs.mkdir(path.dirname(this.filePath), { recursive: true });
    const tempPath = `${this.filePath}.${process.pid}.${Date.now()}.${randomUUID()}.tmp`;
    await fs.writeFile(tempPath, JSON.stringify(records, null, 2), "utf8");
    await fs.rename(tempPath, this.filePath);
  }
}

function scopeKey(userId: string, workspaceId: string): string {
  return `${userId}:${workspaceId}`;
}

function toSnapshot(record: PersistedScopeRecord): WorkspaceStacksSnapshot {
  return {
    stacks: record.stacks
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((stack) => ({
        id: stack.id,
        name: stack.name,
        order: stack.order,
        createdAt: stack.createdAt,
        updatedAt: stack.updatedAt,
      })),
    activeStackId: record.activeStackId,
  };
}
